/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// v1_859: THE ESTABLISHING PERSON, AND THE MLRO'S DESIGNATION OF ANOTHER ONE.
// The establishing person (FINMA Circular 2016/7 online identification, VQF
// 902.1) was always the onboarding contact (session.contact_name and
// contact_email), with nothing stored of its own, so a contact whose identity
// check was rejected (Boris Mozer, rejected on PEP grounds, production
// 28/09/26) held online identification open for good: nobody could complete
// it. The MLRO may now designate another person from the case's verified
// people, a director or authorised signatory whose identity check is approved,
// with a reason. The designation lives at collected_data.establishingPerson;
// the onboarding contact is unchanged and stays on the identity list with
// their own check on record.

import { samePerson as defaultSamePerson } from './person-name-match.js';

export const VERIFIED_STATUSES = ['approved', 'approved_with_flags'];

const str = (v) => String(v == null ? '' : v).trim();
const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str(v));

// Plain status words for notes and the Audit Record (no symbols).
export function checkStatusWords(status) {
  const s = str(status).toLowerCase();
  if (s === 'approved') return 'approved';
  if (s === 'approved_with_flags') return 'approved with flags';
  if (s === 'rejected') return 'rejected';
  if (s === 'requires_review') return 'awaiting review';
  if (s === 'in_progress') return 'in progress';
  if (s === 'link_generated' || s === 'pending') return 'link sent, not completed';
  return 'not started';
}

// The designation on the case, or null.
export function designationOf(session) {
  const cd = (session && session.collected_data && typeof session.collected_data === 'object') ? session.collected_data : {};
  const d = cd.establishingPerson;
  return (d && typeof d === 'object' && str(d.name)) ? d : null;
}

// Who the establishing person is now: the MLRO's designation, else the
// onboarding contact.
export function establishingPersonOf(session) {
  const s = session || {};
  const d = designationOf(s);
  const contact = { name: str(s.contact_name), email: looksLikeEmail(s.contact_email) ? str(s.contact_email) : null };
  if (d) return { name: str(d.name), email: looksLikeEmail(d.email) ? str(d.email) : null, role: str(d.role) || null, designated: true, designation: d, contact };
  return { name: contact.name, email: contact.email, role: 'Authorised signatory (establishing person)', designated: false, designation: null, contact };
}

// Is this name (or email) the establishing person, designated or the contact?
export function isEstablishingPersonNow(session, name, email, match = defaultSamePerson) {
  const est = establishingPersonOf(session);
  const e = str(email).toLowerCase();
  if (est.name && name && match(name, est.name)) return true;
  if (est.email && e && est.email.toLowerCase() === e) return true;
  return false;
}

// The people the MLRO could designate: the natural persons on the file's
// directors and authorised signatories lists (not a party the officer
// removed), each with their identity check status. verificationOf(name, email)
// returns the person's best verification row (or null); eligible means an
// approved check. The current establishing person is marked current.
export function designationCandidates(session, verificationOf, match = defaultSamePerson) {
  const s = session || {};
  const r = (s.report && typeof s.report === 'object') ? s.report : {};
  const cd = (s.collected_data && typeof s.collected_data === 'object') ? s.collected_data : {};
  const removed = Object.values((cd.partyRemovals && typeof cd.partyRemovals === 'object') ? cd.partyRemovals : {}).filter(x => x && x.name).map(x => x.name);
  const corporate = /\b(ltd|limited|llc|inc|plc|gmbh|ag|sa|bv|nv|llp|lp|corp|corporation|company|holdings?|trust|foundation|s\.?a\.?r\.?l)\b\.?$/i;
  const out = [];
  const add = (p, fallbackRole) => {
    const e = (p && typeof p === 'object') ? p : { name: p };
    const name = str(e.name);
    if (!name || corporate.test(name)) return;
    if (e.kind && /corporate|entity|company/i.test(String(e.kind))) return;
    if (removed.some(rn => match(rn, name))) return;
    if (out.some(c => match(c.name, name))) return;
    const v = typeof verificationOf === 'function' ? verificationOf(name, e.email) : null;
    const status = str(v && v.status).toLowerCase();
    out.push({
      name,
      role: str(e.role) || fallbackRole,
      email: looksLikeEmail(e.email) ? str(e.email) : (v && looksLikeEmail(v.person_email) ? str(v.person_email) : null),
      status,
      eligible: VERIFIED_STATUSES.includes(status),
      current: isEstablishingPersonNow(s, name, e.email, match),
    });
  };
  for (const d of (Array.isArray(r.directors) ? r.directors : [])) add(d, 'Director');
  for (const a of (Array.isArray(r.authorisedSignatories) ? r.authorisedSignatories : [])) add(a, 'Authorised signatory');
  return out;
}

// The panel's line where nobody can be designated yet: who could be, once
// verified. Empty string when someone is eligible.
export function noVerifiedCandidateLine(candidates) {
  const list = (candidates || []).filter(c => !c.current);
  if (list.some(c => c.eligible)) return '';
  if (!list.length) return 'No other director or authorised signatory is on the file, so there is nobody to designate yet.';
  const who = list.map(c => `${c.name} (${c.role}, identity check ${checkStatusWords(c.status)})`).join('; ');
  return `No director or authorised signatory has an approved identity check yet, so nobody can be designated. Once verified, any of these could be designated: ${who}.`;
}

// Validate a designation request against the file. Returns { error } or
// { candidate }.
export function validateDesignation(session, { name, reason } = {}, verificationOf, match = defaultSamePerson) {
  const why = str(reason);
  if (!str(name)) return { error: 'Choose the person to designate.' };
  if (why.length < 10 || why.split(/\s+/).filter(Boolean).length < 3) return { error: 'A reason is required: say why the establishing person is changing (a few words at least). It is recorded on the case notes and the Audit Record.' };
  const candidates = designationCandidates(session, verificationOf, match);
  const c = candidates.find(x => match(x.name, name));
  if (!c) return { error: `${str(name)} is not a director or authorised signatory on this file, so they cannot be designated.` };
  if (c.current) return { error: `${c.name} is already the establishing person.` };
  if (!c.eligible) return { error: `${c.name}'s identity check is not approved (${checkStatusWords(c.status)}). Only a person whose identity check is approved can be designated.` };
  return { candidate: c };
}

// The designation record written to collected_data.establishingPerson.
export function designationRecord(session, candidate, { reason, by, at, previousVerification } = {}) {
  const prevEst = establishingPersonOf(session);
  const prior = designationOf(session);
  const previous = {
    name: prevEst.name || null,
    email: prevEst.email || null,
    role: prevEst.designated ? (prevEst.role || null) : 'Authorised signatory (establishing person)',
    source: prevEst.designated ? 'designated by the MLRO' : 'the onboarding contact',
    checkStatus: str(previousVerification && previousVerification.status).toLowerCase() || null,
  };
  const history = [...((prior && Array.isArray(prior.history)) ? prior.history : []), ...(prior ? [{ name: prior.name, role: prior.role || null, designatedBy: prior.designatedBy || null, designatedAt: prior.designatedAt || null, reason: prior.reason || null }] : [])];
  return {
    name: candidate.name,
    email: candidate.email || null,
    role: candidate.role || null,
    checkStatus: candidate.status || null,
    designatedBy: str(by) || 'MLRO',
    designatedAt: at || new Date().toISOString(),
    reason: str(reason),
    previous,
    ...(history.length ? { history } : {}),
  };
}

// The case note for a designation. The payment reference is named as
// unchanged (the transfer comes from the company account, not the person).
export function designationNote(record, { paymentRef } = {}) {
  const p = record.previous || {};
  const prevCheck = p.checkStatus ? `identity check ${checkStatusWords(p.checkStatus)}` : 'no identity check on record';
  return `Establishing person designated by ${record.designatedBy}: ${record.name} (${record.role || 'role not recorded'}, identity check ${checkStatusWords(record.checkStatus)}) replaces ${p.name || 'nobody recorded'} (${p.source || 'source not recorded'}, ${prevCheck}) for online identification (FINMA Circular 2016/7).\nMLRO reason: ${record.reason}\n${p.name ? `${p.name} stays on the identity list with that check on record. ` : ''}The confirmation payment reference ${paymentRef ? paymentRef + ' is' : 'is not generated yet and will be'} unchanged by this, as the payment comes from the company account.`;
}

export default establishingPersonOf;
